/**
 * @file 紙面の左右に「前へ / 次へ」を置く余白があるかを測る hook である。
 *
 * @what: 紙面の器 (stage) の横幅と、画像の実際に塗られる幅 (object-fit: contain で収めた
 *   ときの幅) から、左右それぞれの余白を出す。余白がボタンの幅に足りなければ false、足りれば
 *   true を返す。画像の読み込み前や、寸法が取れない環境 (テストの jsdom) では null を返す。
 *   画像の読み込み・器の大きさの変化・画面の大きさの変化で測り直す。
 *   見開きのときは画像ではなく2枚の並び (縦横比のとおりの箱) を渡し、その幅を塗られる幅とする。
 * @why: 紙面が横幅いっぱいのとき、紙面に重ねた矢印が本文にかぶる (代表の指摘、2026-09-17)。
 *   余白が無いときは矢印を出さず、紙面のタップでページを送る (Kindle の作法) ので、
 *   その切り替えの判定をここに集める。px の閾値ではなく実測にするのは、iPad の縦画面
 *   (横幅768〜834px) でも紙面が横幅いっぱいになり、幅では取りこぼすためである。
 * @failure: 測れないときは null を返し、呼び出し側が従来の矢印を出す。例外は投げない。
 */

import { useEffect, useState, type RefObject } from "react";

/** 矢印のボタンを置くのに必要な片側の余白 (px)。文言付きと、狭い画面の矢印だけのとき。 */
const NAV_ROOM_PX = 104;
const NAV_ROOM_NARROW_PX = 52;

/**
 * 紙面の左右に矢印を置く余白があるか。
 * @param stageRef 紙面の器
 * @param imgRef 紙面の画像 (見開きのときは2枚の並び)
 * @param imageUrl いま出している画像 (変わったら測り直す。見開きへの切り替えも別の値で渡す)
 * @param zoom 紙面の倍率 (変わったら測り直す)
 */
export function useNavRoom(
  stageRef: RefObject<HTMLElement | null>,
  imgRef: RefObject<HTMLElement | null>,
  imageUrl: string | null,
  zoom: number,
): boolean | null {
  const [room, setRoom] = useState<boolean | null>(null);
  useEffect(() => {
    const stage = stageRef.current;
    const img = imgRef.current;
    if (!stage || !img || imageUrl === null) {
      setRoom(null);
      return;
    }
    const measure = () => setRoom(measureRoom(stage, img));
    measure();
    img.addEventListener("load", measure);
    window.addEventListener("resize", measure);
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(stage);
      // 見開きの並びは、2枚の縦横比が分かったところで幅が変わる
      if (!(img instanceof HTMLImageElement)) ro.observe(img);
    }
    return () => {
      img.removeEventListener("load", measure);
      window.removeEventListener("resize", measure);
      ro?.disconnect();
    };
  }, [stageRef, imgRef, imageUrl, zoom]);
  return room;
}

function measureRoom(stage: HTMLElement, img: HTMLElement): boolean | null {
  const stageW = stage.clientWidth;
  const boxW = img.clientWidth;
  const boxH = img.clientHeight;
  if (stageW === 0 || boxW === 0 || boxH === 0) return null;
  let painted = boxW;
  if (img instanceof HTMLImageElement) {
    if (img.naturalWidth === 0 || img.naturalHeight === 0) return null;
    // object-fit: contain で収めたときに塗られる幅
    painted = Math.min(boxW, (boxH * img.naturalWidth) / img.naturalHeight);
  }
  const narrow =
    typeof window.matchMedia === "function" && window.matchMedia("(max-width: 600px)").matches;
  return (stageW - painted) / 2 >= (narrow ? NAV_ROOM_NARROW_PX : NAV_ROOM_PX);
}
